import type { MailMessageDetail } from "@/lib/types/mail";
import type { ComposeDraft, ComposeMode } from "@/lib/types/compose";
import {
  buildNewComposeBody,
  buildReplyComposeBody,
  htmlToPlainText,
  mergeLegacyComposeQuoteHtml,
  plainTextToHtml,
  stripMailDisplayDocumentWrapper,
  wrapQuoteHtml,
} from "@/lib/compose/composeHtml";

/** Extrait l’adresse depuis « Nom <email@domaine.fr> ». */
export function extractEmailAddress(raw: string): string {
  const trimmed = raw.trim();
  const match = trimmed.match(/<([^>]+)>/);
  if (match?.[1]) return match[1].trim();
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return trimmed;
  return trimmed;
}

function replySubject(subject: string): string {
  const s = subject.trim();
  if (/^re:/i.test(s)) return s;
  return `Re: ${s || "(Sans objet)"}`;
}

function forwardSubject(subject: string): string {
  const s = subject.trim();
  if (/^fwd:/i.test(s) || /^tr:/i.test(s)) return s;
  return `Fwd: ${s || "(Sans objet)"}`;
}

function quoteBodyText(message: MailMessageDetail): string {
  const header = [
    "",
    "---------- Message d'origine ----------",
    `De : ${message.from}`,
    message.to ? `À : ${message.to}` : "",
    message.date ? `Date : ${message.date}` : "",
    `Objet : ${message.subject}`,
    "",
  ]
    .filter(Boolean)
    .join("\n");

  const body =
    message.bodyText.trim() ||
    htmlToPlainText(message.bodyHtml).trim() ||
    message.bodyHtml.replace(/<[^>]+>/g, " ").trim();
  return `${header}\n${body}`;
}

function quoteBodyHtml(message: MailMessageDetail): string {
  const header = [
    "<p><strong>---------- Message d'origine ----------</strong></p>",
    `<p><strong>De :</strong> ${message.from}</p>`,
    message.to ? `<p><strong>À :</strong> ${message.to}</p>` : "",
    message.date ? `<p><strong>Date :</strong> ${message.date}</p>` : "",
    `<p><strong>Objet :</strong> ${message.subject}</p>`,
    "<p><br></p>",
  ]
    .filter(Boolean)
    .join("");

  const body = message.bodyHtml.trim()
    ? stripMailDisplayDocumentWrapper(message.bodyHtml)
    : plainTextToHtml(message.bodyText.trim());

  return `${header}${body}`;
}

export type BuildComposeDraftOptions = {
  accountEmail?: string;
  signature?: string;
};

export function buildComposeDraft(
  mode: ComposeMode,
  message: MailMessageDetail | null,
  options: BuildComposeDraftOptions = {}
): ComposeDraft {
  const signature = options.signature?.trim() ?? "";

  if (mode === "new" || !message) {
    const { bodyHtml, bodyText } = buildNewComposeBody(signature);
    return {
      mode: "new",
      to: "",
      cc: "",
      bcc: "",
      subject: "",
      bodyText,
      bodyHtml,
    };
  }

  const from = extractEmailAddress(message.from);
  const self = options.accountEmail
    ? extractEmailAddress(options.accountEmail).toLowerCase()
    : "";
  const quoteText = quoteBodyText(message);
  const quoteHtml = quoteBodyHtml(message);
  const { bodyHtml, bodyText } = buildReplyComposeBody(signature, quoteText, quoteHtml);

  if (mode === "reply") {
    return {
      mode,
      to: from,
      cc: "",
      bcc: "",
      subject: replySubject(message.subject),
      bodyText,
      bodyHtml,
      inReplyTo: message.messageId,
      references: message.messageId,
    };
  }

  if (mode === "replyAll") {
    const toParts = new Set<string>();
    if (from) toParts.add(from);
    for (const addr of message.to.split(",")) {
      const email = extractEmailAddress(addr);
      if (email && email.toLowerCase() !== self) toParts.add(email);
    }
    const ccParts = new Set<string>();
    if (message.cc) {
      for (const addr of message.cc.split(",")) {
        const email = extractEmailAddress(addr);
        if (email && email.toLowerCase() !== self && !toParts.has(email)) {
          ccParts.add(email);
        }
      }
    }

    return {
      mode,
      to: [...toParts].join(", "),
      cc: [...ccParts].join(", "),
      bcc: "",
      subject: replySubject(message.subject),
      bodyText,
      bodyHtml,
      inReplyTo: message.messageId,
      references: message.messageId,
    };
  }

  const quoteBlock = wrapQuoteHtml(quoteHtml || plainTextToHtml(quoteText));
  const { bodyHtml: editorHtml, bodyText: editorText } = buildNewComposeBody(signature);
  const mergedHtml = mergeLegacyComposeQuoteHtml(editorHtml, quoteBlock);

  return {
    mode: "forward",
    to: "",
    cc: "",
    bcc: "",
    subject: forwardSubject(message.subject),
    bodyText: editorText,
    bodyHtml: mergedHtml,
    composeQuoteHtml: quoteBlock,
  };
}

/** Réponse / répondre à tous : éditeur sans citation (affichée sous le lecteur). */
export function buildInlineReplyComposeDraft(
  mode: "reply" | "replyAll",
  message: MailMessageDetail,
  options: BuildComposeDraftOptions = {}
): ComposeDraft {
  const modalDraft = buildComposeDraft(mode, message, options);
  const { bodyHtml, bodyText } = buildNewComposeBody(options.signature?.trim() ?? "");
  const quoteInner = quoteBodyHtml(message);
  return {
    ...modalDraft,
    bodyHtml,
    bodyText,
    composeQuoteHtml: wrapQuoteHtml(quoteInner),
  };
}

/** Ouverture immédiate du transfert pendant chargement IMAP du corps cité. */
export function buildForwardComposeLoadingDraft(
  subject: string,
  options: BuildComposeDraftOptions = {}
): ComposeDraft {
  const { bodyHtml, bodyText } = buildNewComposeBody(options.signature?.trim() ?? "");
  return {
    mode: "forward",
    to: "",
    cc: "",
    bcc: "",
    subject: forwardSubject(subject),
    bodyText,
    bodyHtml,
    bodyLoading: true,
  };
}

/** Brouillon partiel depuis la liste (ouverture immédiate, corps chargé ensuite). */
export function buildComposeDraftFromSummary(
  summary: { subject: string },
  folder: string,
  uid: number
): ComposeDraft {
  return {
    mode: "new",
    to: "",
    cc: "",
    bcc: "",
    subject: summary.subject,
    bodyText: "",
    bodyHtml: "",
    draftFolder: folder,
    draftUid: uid,
  };
}

/** Charge un brouillon IMAP dans l’éditeur de rédaction. */
export function buildComposeDraftFromImapDraft(
  message: MailMessageDetail,
  folder: string,
  uid: number,
  attachments: ComposeDraft["attachments"] = []
): ComposeDraft {
  return {
    mode: "new",
    to: message.to.trim(),
    cc: message.cc?.trim() ?? "",
    bcc: "",
    subject: message.subject,
    bodyText: message.bodyText,
    bodyHtml: stripMailDisplayDocumentWrapper(message.bodyHtml),
    attachments: attachments.length > 0 ? attachments : undefined,
    inReplyTo: message.messageId || undefined,
    references: message.messageId || undefined,
    draftFolder: folder,
    draftUid: uid,
  };
}
